import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";
import DynamicFieldInputs from "./DynamicFieldInputs";
import CSVImportModal from "./CSVImportModal";
import WhereUsedModal from "./WhereUsedModal";

export const formatUSD = (n) => {
  const num = Number(n || 0);
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Sprint 5.5 — filter dropdown over an active+inactive catalog list
function FilterSelect({ label, testId, value, onChange, options, t }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white min-w-[140px]"
      >
        <option value="all">{t("products_filter_all")}</option>
        {(options || []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

// Sprint 5.2 — generic dropdown for a V2 catalog (filters inactive but keeps current selection visible)
function V2Select({ label, testId, value, onChange, options, t }) {
  const safeOptions = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    return list.filter((o) => o.is_active || o.id === value);
  }, [options, value]);
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || "")}
        className="w-full border border-[var(--border)] px-3 py-2 font-display bg-white"
      >
        <option value="">{t("no_selection")}</option>
        {safeOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {!o.is_active ? ` (${t("inactive")})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function ProductForm({ initial, categories, productTypes, v2, onSubmit, onCancel }) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name || "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || "");
  const [cost, setCost] = useState(
    initial?.average_cost !== undefined && initial?.average_cost !== null
      ? String(initial.average_cost)
      : ""
  );
  const [description, setDescription] = useState(initial?.description || "");
  // Sprint 3 — type + dynamic custom fields
  const [productTypeId, setProductTypeId] = useState(initial?.product_type_id || "");
  const [customFields, setCustomFields] = useState(initial?.custom_fields || {});
  // Sprint 4 — catalog pricing (sku + price + margin, with auto-coupling on edit)
  const [sku, setSku] = useState(initial?.sku || "");
  const [suggestedPrice, setSuggestedPrice] = useState(
    initial?.suggested_price !== undefined && initial?.suggested_price !== null
      ? String(initial.suggested_price)
      : ""
  );
  const [suggestedMargin, setSuggestedMargin] = useState(
    initial?.suggested_margin_pct !== undefined && initial?.suggested_margin_pct !== null
      ? String(initial.suggested_margin_pct)
      : ""
  );
  // Sprint 5.2 — V2 Master Catalog attributes
  const [itemTypeId, setItemTypeId] = useState(initial?.item_type_id || "");
  const [brandId, setBrandId] = useState(initial?.brand_id || "");
  const [supplierId, setSupplierId] = useState(initial?.supplier_id || "");
  const [colorId, setColorId] = useState(initial?.color_id || "");
  const [sizeId, setSizeId] = useState(initial?.size_id || "");
  const [uomId, setUomId] = useState(initial?.unit_of_measure_id || "");
  const [model, setModel] = useState(initial?.model || "");
  const [requiresDimensions, setRequiresDimensions] = useState(!!initial?.requires_dimensions);
  const [submitting, setSubmitting] = useState(false);

  // Auto-couple price ↔ margin when user types in one
  // Margin formula: margin% = (price - cost) / price * 100  →  price = cost / (1 - margin/100)
  const handlePriceChange = (val) => {
    setSuggestedPrice(val);
    const p = parseFloat(val);
    const c = parseFloat(cost);
    if (!Number.isNaN(p) && p > 0 && !Number.isNaN(c) && c >= 0) {
      const m = ((p - c) / p) * 100;
      setSuggestedMargin(m.toFixed(2));
    }
  };

  const handleMarginChange = (val) => {
    setSuggestedMargin(val);
    const m = parseFloat(val);
    const c = parseFloat(cost);
    if (!Number.isNaN(m) && m >= 0 && m < 100 && !Number.isNaN(c) && c > 0) {
      const p = c / (1 - m / 100);
      setSuggestedPrice(p.toFixed(2));
    }
  };

  // When cost changes, keep margin steady and recompute price (so the user's margin intent is preserved)
  const handleCostChange = (val) => {
    setCost(val);
    const m = parseFloat(suggestedMargin);
    const c = parseFloat(val);
    if (!Number.isNaN(m) && m >= 0 && m < 100 && !Number.isNaN(c) && c > 0) {
      const p = c / (1 - m / 100);
      setSuggestedPrice(p.toFixed(2));
    }
  };

  // Reset custom_fields when type changes (unless we're loading the initial value)
  const handleTypeChange = (newId) => {
    if (newId !== productTypeId) {
      setProductTypeId(newId);
      setCustomFields({});
    }
  };

  // Only active categories OR the currently-selected category (so editing a
  // product attached to an inactive cat doesn't break the dropdown).
  const selectable = useMemo(() => {
    return categories.filter((c) => c.is_active || c.id === categoryId);
  }, [categories, categoryId]);

  // Active types + current one if inactive
  const selectableTypes = useMemo(() => {
    return productTypes.filter((pt) => pt.is_active || pt.id === productTypeId);
  }, [productTypes, productTypeId]);

  const activeSchema = useMemo(() => {
    const pt = productTypes.find((p) => p.id === productTypeId);
    return pt?.field_schema || [];
  }, [productTypes, productTypeId]);

  const handle = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t("financial_product_name"));
      return;
    }
    if (!categoryId) {
      toast.error(t("select_category_placeholder"));
      return;
    }
    const parsedCost = parseFloat(cost);
    if (Number.isNaN(parsedCost) || parsedCost < 0) {
      toast.error(t("cost_must_be_non_negative"));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmedName,
        category_id: categoryId,
        average_cost: parsedCost,
        description: description.trim(),
        product_type_id: productTypeId || null,
        custom_fields: productTypeId ? customFields : {},
        sku: sku.trim(),
        suggested_price: parseFloat(suggestedPrice) || 0,
        suggested_margin_pct: parseFloat(suggestedMargin) || 0,
        // Sprint 5.2 — V2 Master Catalog attributes
        item_type_id: itemTypeId || null,
        brand_id: brandId || null,
        supplier_id: supplierId || null,
        color_id: colorId || null,
        size_id: sizeId || null,
        unit_of_measure_id: uomId || null,
        model: model.trim(),
        requires_dimensions: !!requiresDimensions,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <label className="label-overline block mb-1">{t("financial_product_name")}</label>
        <input
          data-testid={DASHBOARD.financialProductFormName}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-display"
          placeholder={t("financial_product_name")}
        />
      </div>

      <div>
        <label className="label-overline block mb-1">{t("category")}</label>
        <select
          data-testid={DASHBOARD.financialProductFormCategory}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-display bg-white"
        >
          <option value="">{t("select_category_placeholder")}</option>
          {selectable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {!c.is_active ? ` (${t("inactive")})` : ""}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="label-overline block mb-1">{t("product_type_optional")}</label>
        <select
          data-testid={DASHBOARD.financialProductFormType}
          value={productTypeId}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-display bg-white"
        >
          <option value="">{t("select_product_type_placeholder")}</option>
          {selectableTypes.map((pt) => (
            <option key={pt.id} value={pt.id}>
              {pt.name}
              {!pt.is_active ? ` (${t("inactive")})` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Conditional dynamic fields — Sprint 3 smart types */}
      {productTypeId && activeSchema.length > 0 && (
        <DynamicFieldInputs
          schema={activeSchema}
          values={customFields}
          onChange={(k, v) => setCustomFields((prev) => ({ ...prev, [k]: v }))}
          testIdPrefix="financial-product-cf"
        />
      )}

      <div>
        <label className="label-overline block mb-1">{t("sku")}</label>
        <input
          data-testid={DASHBOARD.financialProductFormSku}
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          className="w-full border border-[var(--border)] px-3 py-2 font-mono-tech uppercase tracking-wider"
          placeholder="LOGO-01"
        />
        <div className="text-xs text-[var(--text-secondary)] mt-1">{t("sku_hint")}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label-overline block mb-1">{t("average_cost")}</label>
          <div className="flex items-center border border-[var(--border)]">
            <span className="px-3 py-2 font-mono-tech bg-[var(--bg)] border-r border-[var(--border)]">
              $
            </span>
            <input
              data-testid={DASHBOARD.financialProductFormCost}
              value={cost}
              onChange={(e) => handleCostChange(e.target.value)}
              inputMode="decimal"
              type="number"
              step="0.01"
              min="0"
              className="flex-1 px-3 py-2 font-mono-tech tabular-nums w-full"
              placeholder="0.00"
            />
          </div>
        </div>
        <div>
          <label className="label-overline block mb-1">{t("suggested_price")}</label>
          <div className="flex items-center border border-[var(--border)]">
            <span className="px-3 py-2 font-mono-tech bg-[var(--bg)] border-r border-[var(--border)]">
              $
            </span>
            <input
              data-testid={DASHBOARD.financialProductFormSuggestedPrice}
              value={suggestedPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              inputMode="decimal"
              type="number"
              step="0.01"
              min="0"
              className="flex-1 px-3 py-2 font-mono-tech tabular-nums w-full"
              placeholder="0.00"
            />
          </div>
        </div>
        <div>
          <label className="label-overline block mb-1">{t("suggested_margin")}</label>
          <div className="flex items-center border border-[var(--border)]">
            <input
              data-testid={DASHBOARD.financialProductFormSuggestedMargin}
              value={suggestedMargin}
              onChange={(e) => handleMarginChange(e.target.value)}
              inputMode="decimal"
              type="number"
              step="0.01"
              min="0"
              max="99.99"
              className="flex-1 px-3 py-2 font-mono-tech tabular-nums w-full"
              placeholder="0.00"
            />
            <span className="px-3 py-2 font-mono-tech bg-[var(--bg)] border-l border-[var(--border)]">
              %
            </span>
          </div>
        </div>
      </div>
      <div className="text-xs text-[var(--text-secondary)] -mt-2">
        {t("suggested_margin_hint")}
      </div>

      <div>
        <label className="label-overline block mb-1">{t("long_description")}</label>
        <textarea
          data-testid={DASHBOARD.financialProductFormDescription}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full border border-[var(--border)] px-3 py-2 font-display resize-y"
          placeholder={t("long_description_hint")}
        />
        <div className="text-xs text-[var(--text-secondary)] mt-1">{t("long_description_hint")}</div>
      </div>

      {/* Sprint 5.2 — V2 Master Catalog attributes */}
      <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-4 space-y-3">
        <div>
          <div className="label-overline">{t("v2_master_section")}</div>
          <div className="text-xs text-[var(--text-secondary)] mt-1">{t("v2_master_hint")}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <V2Select
            label={t("field_item_type")}
            testId={DASHBOARD.financialProductFormItemType}
            value={itemTypeId}
            onChange={setItemTypeId}
            options={v2.itemTypes}
            t={t}
          />
          <V2Select
            label={t("field_brand")}
            testId={DASHBOARD.financialProductFormBrand}
            value={brandId}
            onChange={setBrandId}
            options={v2.brands}
            t={t}
          />
          <V2Select
            label={t("field_supplier")}
            testId={DASHBOARD.financialProductFormSupplier}
            value={supplierId}
            onChange={setSupplierId}
            options={v2.suppliers}
            t={t}
          />
          <V2Select
            label={t("field_color")}
            testId={DASHBOARD.financialProductFormColor}
            value={colorId}
            onChange={setColorId}
            options={v2.colors}
            t={t}
          />
          <V2Select
            label={t("field_size")}
            testId={DASHBOARD.financialProductFormSize}
            value={sizeId}
            onChange={setSizeId}
            options={v2.sizes}
            t={t}
          />
          <V2Select
            label={t("field_unit_of_measure")}
            testId={DASHBOARD.financialProductFormUom}
            value={uomId}
            onChange={setUomId}
            options={v2.uoms}
            t={t}
          />
          <div>
            <label className="label-overline block mb-1">{t("field_model")}</label>
            <input
              data-testid={DASHBOARD.financialProductFormModel}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full border border-[var(--border)] px-3 py-2 font-display"
              placeholder="5000, G500…"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer border border-[var(--border)] bg-white px-3 py-2 w-full hover:bg-[var(--bg)] transition-colors">
              <input
                data-testid={DASHBOARD.financialProductFormRequiresDimensions}
                type="checkbox"
                checked={requiresDimensions}
                onChange={(e) => setRequiresDimensions(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-display text-sm font-bold">{t("field_requires_dimensions")}</span>
            </label>
          </div>
        </div>
        {requiresDimensions ? (
          <div className="text-[0.7rem] font-mono-tech text-[var(--accent-cyan,#0E5FFF)] bg-white border border-[var(--border)] px-2 py-1.5">
            ⓘ {t("field_requires_dimensions_hint")}
          </div>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-testid={DASHBOARD.financialProductFormCancel}
          onClick={onCancel}
          className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--bg)]"
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid={DASHBOARD.financialProductFormSubmit}
          className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {t("save")}
        </button>
      </div>
    </form>
  );
}

export default function FinancialProductsPanel({ categoriesVersion }) {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  // Sprint 5.2 — V2 Master Catalog references
  const [itemTypes, setItemTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("visible"); // visible | active | inactive | archived | all
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Sprint 5.5 — search + V2 filters
  const [searchQ, setSearchQ] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [colorFilter, setColorFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  // Sprint 5.5 — where-used modal state
  const [whereUsed, setWhereUsed] = useState({ open: false, product: null });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Sprint 5.4 — bulk CSV importer modal
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        pRes, cRes, ptRes,
        itRes, brRes, suRes, coRes, szRes, uoRes,
      ] = await Promise.all([
        axios.get(`${API}/financial-products`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${API}/product-types`, { withCredentials: true }),
        axios.get(`${API}/item-types`, { withCredentials: true }),
        axios.get(`${API}/brands`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/colors`, { withCredentials: true }),
        axios.get(`${API}/sizes`, { withCredentials: true }),
        axios.get(`${API}/units-of-measure`, { withCredentials: true }),
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
      setProductTypes(ptRes.data);
      setItemTypes(itRes.data || []);
      setBrands(brRes.data || []);
      setSuppliers(suRes.data || []);
      setColors(coRes.data || []);
      setSizes(szRes.data || []);
      setUoms(uoRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, categoriesVersion]);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/financial-products/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/financial-products`, payload, { withCredentials: true });
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggle = async (p) => {
    try {
      await axios.patch(`${API}/financial-products/${p.id}/toggle-active`, null, { withCredentials: true });
      toast.success(t("state_changed_ok"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const categoryById = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categories]);

  const productTypeById = useMemo(() => {
    const map = {};
    productTypes.forEach((pt) => {
      map[pt.id] = pt;
    });
    return map;
  }, [productTypes]);

  // Sprint 5.2 — V2 lookup map for showing names in the row
  const v2NameById = useMemo(() => {
    const m = {};
    [itemTypes, brands, suppliers, colors, sizes, uoms].forEach((list) => {
      (list || []).forEach((x) => { m[x.id] = x.name; });
    });
    return m;
  }, [itemTypes, brands, suppliers, colors, sizes, uoms]);

  const filtered = products.filter((p) => {
    // Sprint 5.5 — status: visible (default, hides archived), active, inactive, archived, all
    const pStatus = p.status || (p.is_active ? "active" : "inactive");
    if (statusFilter === "visible" && pStatus === "archived") return false;
    if (statusFilter === "active" && pStatus !== "active") return false;
    if (statusFilter === "inactive" && pStatus !== "inactive") return false;
    if (statusFilter === "archived" && pStatus !== "archived") return false;
    // "all" → no status filter

    if (categoryFilter !== "all" && p.category_id !== categoryFilter) return false;
    if (brandFilter !== "all" && p.brand_id !== brandFilter) return false;
    if (colorFilter !== "all" && p.color_id !== colorFilter) return false;
    if (sizeFilter !== "all" && p.size_id !== sizeFilter) return false;
    if (supplierFilter !== "all" && p.supplier_id !== supplierFilter) return false;

    // Search across name / sku / model
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      const hay = `${p.name || ""} ${p.sku || ""} ${p.model || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setSearchQ("");
    setStatusFilter("visible");
    setCategoryFilter("all");
    setBrandFilter("all");
    setColorFilter("all");
    setSizeFilter("all");
    setSupplierFilter("all");
  };

  // Sprint 5.5 — change a product's status (active | inactive | archived)
  const setStatus = async (p, newStatus) => {
    if (newStatus === (p.status || (p.is_active ? "active" : "inactive"))) return;
    // Warn when archiving a product used in orders
    if (newStatus === "archived") {
      try {
        const usage = await axios.get(
          `${API}/financial-products/${p.id}/usage`,
          { withCredentials: true }
        );
        const n = usage.data.orders_count;
        const msg = n > 0
          ? t("where_used_archive_blocked").replace("{n}", String(n)) + "\n\n" + t("archive_product_confirm")
          : t("archive_product_confirm");
        if (!window.confirm(msg)) return;
      } catch {
        if (!window.confirm(t("archive_product_confirm"))) return;
      }
    }
    try {
      await axios.patch(
        `${API}/financial-products/${p.id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(t("state_changed_ok"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const activeCategories = categories.filter((c) => c.is_active);
  const canCreate = activeCategories.length > 0;

  return (
    <div data-testid={DASHBOARD.financialProductsList} className="space-y-4">
      {/* Sprint 5.5 — Search + Filters bar */}
      <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="label-overline block mb-1">{t("search")}</label>
            <input
              data-testid={DASHBOARD.productsSearchInput}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={t("products_search_placeholder")}
              className="w-full border border-[var(--border)] px-3 py-2 font-display text-sm bg-white"
            />
          </div>
          <FilterSelect
            label={t("category")}
            testId={DASHBOARD.financialProductFilterCategory}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categories}
            t={t}
          />
          <FilterSelect
            label={t("products_filter_brand")}
            testId={DASHBOARD.productsFilterBrand}
            value={brandFilter}
            onChange={setBrandFilter}
            options={brands}
            t={t}
          />
          <FilterSelect
            label={t("products_filter_color")}
            testId={DASHBOARD.productsFilterColor}
            value={colorFilter}
            onChange={setColorFilter}
            options={colors}
            t={t}
          />
          <FilterSelect
            label={t("products_filter_size")}
            testId={DASHBOARD.productsFilterSize}
            value={sizeFilter}
            onChange={setSizeFilter}
            options={sizes}
            t={t}
          />
          <FilterSelect
            label={t("products_filter_supplier")}
            testId={DASHBOARD.productsFilterSupplier}
            value={supplierFilter}
            onChange={setSupplierFilter}
            options={suppliers}
            t={t}
          />
          <div>
            <label className="label-overline block mb-1">{t("products_filter_status")}</label>
            <select
              data-testid={DASHBOARD.productsFilterStatus}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
            >
              <option value="visible">{t("products_status_visible")}</option>
              <option value="active">{t("products_status_active")}</option>
              <option value="inactive">{t("products_status_inactive")}</option>
              <option value="archived">{t("products_status_archived")}</option>
              <option value="all">{t("products_status_any")}</option>
            </select>
          </div>
          <button
            type="button"
            data-testid={DASHBOARD.productsFiltersReset}
            onClick={resetFilters}
            className="border border-[var(--border)] px-3 py-2 font-mono-tech text-xs uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
          >
            ✕ {t("products_filters_reset")}
          </button>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            data-testid={DASHBOARD.productsImportButton}
            onClick={() => setImportOpen(true)}
            className="border border-black px-4 py-2.5 font-display font-bold tracking-wide hover:bg-black hover:text-white transition-colors text-sm"
            title={t("csv_import_button")}
          >
            ↑ {t("csv_import_button")}
          </button>
          <button
            data-testid={DASHBOARD.financialProductNewBtn}
            disabled={!canCreate}
            onClick={() => {
              setEditing({});
              setOpen(true);
            }}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={canCreate ? "" : t("no_active_category_warning")}
          >
            + {t("new_financial_product")}
          </button>
        </div>
      </div>

      {!canCreate && (
        <div className="border border-[var(--alert-overdue)] bg-white px-4 py-3 text-sm text-[var(--alert-overdue)]">
          {t("no_active_category_warning")}
        </div>
      )}

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {products.length === 0 ? t("no_financial_products") : t("products_no_matches")}
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
          {filtered.map((p) => {
            const cat = categoryById[p.category_id];
            const ptype = p.product_type_id ? productTypeById[p.product_type_id] : null;
            const schema = ptype?.field_schema || [];
            const cfSummary = schema
              .map((f) => {
                const v = p.custom_fields?.[f.key];
                if (v === undefined || v === null || v === "") return null;
                return `${f.label}: ${v}${f.unit ? ` ${f.unit}` : ""}`;
              })
              .filter(Boolean)
              .join(" · ");
            // Sprint 5.2 — V2 attribute summary
            const v2Parts = [
              p.brand_id && v2NameById[p.brand_id],
              p.model,
              p.color_id && v2NameById[p.color_id],
              p.size_id && v2NameById[p.size_id],
            ].filter(Boolean);
            const v2Summary = v2Parts.join(" · ");
            const supplierName = p.supplier_id && v2NameById[p.supplier_id];
            return (
              <div
                key={p.id}
                data-testid={DASHBOARD.financialProductRow(p.id)}
                className={`grid grid-cols-12 gap-4 items-start px-5 py-4 hover:bg-[var(--bg)] ${
                  (p.status || (p.is_active ? "active" : "inactive")) === "archived"
                    ? "opacity-70 bg-amber-50/50"
                    : p.is_active
                    ? ""
                    : "opacity-60"
                }`}
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-lg font-bold tracking-tight">{p.name}</div>
                  <div className="font-mono-tech text-xs text-[var(--text-secondary)] mt-0.5">
                    {p.sku ? (
                      <span className="mr-2 px-1.5 py-0.5 bg-black text-white text-[0.6rem] tracking-wider">
                        {p.sku}
                      </span>
                    ) : null}
                    {cat ? cat.name : "—"}
                    {ptype ? (
                      <span className="ml-2 px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border)] text-[0.6rem] tracking-wide">
                        {ptype.name.toUpperCase()}
                      </span>
                    ) : null}
                  </div>
                  {cfSummary ? (
                    <div className="text-[0.7rem] font-mono-tech text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">
                      {cfSummary}
                    </div>
                  ) : null}
                  {v2Summary ? (
                    <div className="text-[0.7rem] font-mono-tech text-black mt-1 flex flex-wrap gap-1">
                      {v2Summary.split(" · ").map((part, i) => (
                        <span key={i} className="bg-white border border-[var(--border)] px-1.5 py-0.5">
                          {part}
                        </span>
                      ))}
                      {p.requires_dimensions ? (
                        <span className="bg-[var(--accent-cyan,#0E5FFF)] text-white px-1.5 py-0.5 font-bold">
                          ⌗ {t("field_requires_dimensions")}
                        </span>
                      ) : null}
                    </div>
                  ) : p.requires_dimensions ? (
                    <div className="text-[0.7rem] font-mono-tech mt-1">
                      <span className="bg-[var(--accent-cyan,#0E5FFF)] text-white px-1.5 py-0.5 font-bold">
                        ⌗ {t("field_requires_dimensions")}
                      </span>
                    </div>
                  ) : null}
                  {supplierName ? (
                    <div className="text-[0.65rem] text-[var(--text-secondary)] mt-0.5">
                      {t("field_supplier")}: {supplierName}
                    </div>
                  ) : null}
                  {p.description ? (
                    <div className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2 whitespace-pre-wrap">
                      {p.description}
                    </div>
                  ) : null}
                </div>
                <div className="col-span-6 md:col-span-2 font-mono-tech tabular-nums text-sm">
                  <div className="text-[0.6rem] tracking-widest text-[var(--text-secondary)]">{t("average_cost")}</div>
                  <div className="font-bold">{formatUSD(p.average_cost)}</div>
                  {(p.suggested_price > 0 || p.suggested_margin_pct > 0) ? (
                    <div className="text-[0.65rem] text-[var(--text-secondary)] mt-0.5">
                      {t("suggested_price")}: <span className="font-bold text-[var(--text-primary)]">{formatUSD(p.suggested_price)}</span>
                      {p.suggested_margin_pct ? ` · ${Number(p.suggested_margin_pct).toFixed(1)}%` : ""}
                    </div>
                  ) : null}
                </div>
                <div className="col-span-6 md:col-span-2">
                  {(() => {
                    const pStatus = p.status || (p.is_active ? "active" : "inactive");
                    return (
                      <select
                        data-testid={DASHBOARD.productStatusSelect(p.id)}
                        value={pStatus}
                        onChange={(e) => setStatus(p, e.target.value)}
                        className={`w-full font-mono-tech text-[0.7rem] tracking-wider px-2 py-1.5 border ${
                          pStatus === "active"
                            ? "bg-black text-white border-black"
                            : pStatus === "archived"
                            ? "bg-amber-100 text-amber-900 border-amber-700"
                            : "bg-white text-[var(--text-secondary)] border-[var(--border)]"
                        }`}
                      >
                        <option value="active">{t("status_active").toUpperCase()}</option>
                        <option value="inactive">{t("status_inactive").toUpperCase()}</option>
                        <option value="archived">{t("status_archived").toUpperCase()}</option>
                      </select>
                    );
                  })()}
                </div>
                <div className="col-span-12 md:col-span-3 flex justify-end gap-2">
                  <button
                    data-testid={DASHBOARD.productWhereUsedBtn(p.id)}
                    onClick={() => setWhereUsed({ open: true, product: p })}
                    className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                    title={t("where_used_title")}
                  >
                    ↗ {t("orders")}
                  </button>
                  <button
                    data-testid={DASHBOARD.financialProductEditBtn(p.id)}
                    onClick={() => {
                      setEditing(p);
                      setOpen(true);
                    }}
                    className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                  >
                    {t("edit")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit_financial_product") : t("new_financial_product")}
        testId="financial-product-modal"
        wide
      >
        <ProductForm
          initial={editing}
          categories={categories}
          productTypes={productTypes}
          v2={{ itemTypes, brands, suppliers, colors, sizes, uoms }}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      </Modal>

      <CSVImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          load();
        }}
      />

      <WhereUsedModal
        open={whereUsed.open}
        product={whereUsed.product}
        onClose={() => setWhereUsed({ open: false, product: null })}
      />
    </div>
  );
}
